import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { varianteService, Variante } from '../../services/varianteService';
import { productoService } from '../../services/productoService';
import VarianteForm from '../../components/Variantes/VarianteForm';

const VariantesList = () => {
  const [productoFilter, setProductoFilter] = useState<number | undefined>(undefined);
  const [showForm, setShowForm] = useState(false);
  const [editingVariante, setEditingVariante] = useState<Variante | null>(null);
  const queryClient = useQueryClient();

  const { data: productosData } = useQuery(
    'productos',
    () => productoService.getAll(1, 200)
  );

  const { data, isLoading } = useQuery(
    ['variantes', productoFilter],
    () => varianteService.getAll(productoFilter)
  );

  const deleteMutation = useMutation(varianteService.delete, {
    onSuccess: (data) => {
      toast.success(data.message || 'Variante eliminada correctamente');
      queryClient.invalidateQueries(['variantes']);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al eliminar variante');
    }
  });

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de eliminar esta variante?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (variante: Variante) => {
    setEditingVariante(variante);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingVariante(null);
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Variantes de Productos</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
        >
          + Nueva Variante
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <select
            value={productoFilter || ''}
            onChange={(e) => setProductoFilter(e.target.value ? Number(e.target.value) : undefined)}
            className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todos los productos</option>
            {productosData?.data.productos.map((producto) => (
              <option key={producto.id} value={producto.id}>
                {producto.name}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre Variante</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código Barras</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio Adicional</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.variantes.map((variante) => {
                const precioFinal = variante.producto?.precioBase 
                  ? Number(variante.producto.precioBase) + Number(variante.precioAdicional)
                  : Number(variante.precioAdicional);
                
                return (
                  <tr key={variante.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {variante.producto?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {variante.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {variante.sku || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {variante.codigoBarras || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div>
                        <span className="text-gray-500">+${Number(variante.precioAdicional).toFixed(2)}</span>
                        {variante.producto?.precioBase && (
                          <div className="text-xs text-gray-400">
                            Total: ${precioFinal.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${variante.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {variante.isActive ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(variante)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(variante.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {data?.data.variantes.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No hay variantes registradas
          </div>
        )}
      </div>

      {showForm && (
        <VarianteForm
          variante={editingVariante}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default VariantesList;

